import { Fragment, useMemo, useState, type ReactNode } from "react";
import { AlertTriangle, Archive, ArrowUpDown, CalendarDays, Crown, FileText, Info, Pencil, CheckCircle2, Loader2, Mic2, RefreshCw, Search, Send, UserPlus, Users, XCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import CrmColumnHeading from "@/components/CrmColumnHeading";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { chiNotify } from "@/components/ChiNotification";
import {
  CRM_ROW_PORTRAIT_SIZE,
  importedMemberPhotoUrl,
  MemberProfilePhoto,
} from "@/components/PersonPortraitAvatar";
import DataTableActions, { type DataTableAction } from "@/components/DataTableActions";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import CrmFilterTiles from "@/components/CrmFilterTiles";
import CrmTablePanel from "@/components/CrmTablePanel";
import { crmHelperTextStyle, crmInputStyle, crmLabelStyle, crmTextareaStyle } from "@/components/crmFormStyles";
import SocialLinksInput, { CompanyWebsiteInput, socialLinkDrafts, socialLinkDraftError, type SocialLinkDraft } from "@/components/SocialLinksInput";
import { parseSocialLinks } from "@shared/socialLinks";
import { memberAttention, type MemberAttention } from "@shared/memberAttention";
import { PhoneInput, LinkedInInput, LINKEDIN_INPUT_HINT, splitStoredPhone, storedPhoneValue, linkedinUsernameFromStored, storedLinkedInValue } from "@/components/ProfileContactInputs";
import { MAX_IMAGE_UPLOAD_BYTES, MAX_IMAGE_UPLOAD_LABEL } from "@shared/imageUploadLimits";
import AddMemberDrawer from "@/components/AddMemberDrawer";
import BiographyAssistant from "@/components/BiographyAssistant";
import AddSpeakerRoleDialog from "@/components/AddSpeakerRoleDialog";
import InterestTypeahead from "@/components/InterestTypeahead";
import { filterMemberRecordsByListView, type MemberListView } from "@/lib/memberArchiveView";
import { MEMBER_INTERESTS } from "@shared/memberInterests";
import { formatPhoneForDisplay } from "@shared/profileInputs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type AccessState = "inactive" | "active" | "past_due" | "cancelled" | "expired";
type PaidFilter = "all" | "paid" | "non_paid";
type SortKey = "name" | "paidStatus" | "createdAt";

/**
 * Browsers and file providers can mislabel an otherwise valid image. Read the
 * same magic bytes the server validates so the data URL always declares the
 * file's real PNG, JPEG, or WebP type.
 */
async function memberPhotoMimeType(file: File): Promise<string | null> {
  try {
    const header = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    const isPng = header.length >= 8
      && header[0] === 137
      && header[1] === 80
      && header[2] === 78
      && header[3] === 71
      && header[4] === 13
      && header[5] === 10
      && header[6] === 26
      && header[7] === 10;
    if (isPng) return "image/png";

    const isJpeg = header.length >= 3 && header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff;
    if (isJpeg) return "image/jpeg";

    const isWebp = header.length >= 12
      && header[0] === 82
      && header[1] === 73
      && header[2] === 70
      && header[3] === 70
      && header[8] === 87
      && header[9] === 69
      && header[10] === 66
      && header[11] === 80;
    if (isWebp) return "image/webp";
  } catch {
    return null;
  }

  /*
   * A filename or browser MIME label is not enough to identify an upload. The
   * server checks the same magic bytes, so returning a type only after reading
   * them prevents a confusing server-side mismatch for mislabeled files.
   */
  return null;
}

const STATUS_LABELS: Record<AccessState, string> = {
  inactive: "Free · active",
  active: "Paid · active",
  past_due: "Paid · payment overdue",
  cancelled: "Cancelled",
  expired: "Expired",
};

/**
 * Numeric and short — 8/10/26.
 *
 * The table carries three date columns side by side. Spelled-out months were
 * each about twice as wide as the number they contained, which is width taken
 * from the name, company and title columns where the reading actually happens.
 */
function formatDate(value: Date | string | null) {
  if (!value) return "Never";
  return new Intl.DateTimeFormat("en-US", { month: "numeric", day: "numeric", year: "2-digit" }).format(new Date(value));
}

/**
 * When a member's paid period next turns over.
 *
 * A renewal date only exists for a paid membership — it is the paid-through date
 * on the Stripe subscription — so a non-paid account shows "N/A" rather than a
 * blank that reads as missing data.
 *
 * `cancelAtPeriodEnd` is the reason this is a component rather than a call to
 * `formatDate`. The same timestamp means opposite things: a renewal date while
 * billing continues, and a last day of access once the member has cancelled.
 * Printing the second under a "Renewal" heading would tell an administrator that
 * a cancelling member renews, so a cancelling member's date is labelled "Ends"
 * and coloured as the warning it is.
 */
function RenewalDate({
  paid,
  currentPeriodEnd,
  cancelAtPeriodEnd,
  pending = false,
}: {
  paid: boolean;
  /** Stripe's own next-billing date when it could be read, else the stored mirror. */
  currentPeriodEnd: Date | string | null;
  cancelAtPeriodEnd: number | null;
  /** Stripe has not answered yet; the mirror may still fill this in. */
  pending?: boolean;
}) {
  const muted = { color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" } as const;

  if (!paid) return <span style={muted}>N/A</span>;

  /*
   * Neither Stripe nor the mirror has a date.
   *
   * This used to read "Awaiting Stripe", which described the application's
   * internal state rather than the member's. It appeared for a member whose
   * subscription Stripe could describe perfectly well — the Payments table was
   * showing their renewal date at the same moment — and named nothing an
   * administrator could act on.
   *
   * "Still checking" and "there is no subscription" are different situations
   * calling for different actions, so they read differently.
   */
  if (!currentPeriodEnd) {
    return pending
      ? <span style={muted}>Checking…</span>
      : <span style={muted} title="This member is marked paid, but no Stripe subscription could be found for them. Check the Payments view.">No subscription found</span>;
  }

  /*
   * A cancelling membership says so here, where its date is.
   *
   * The same timestamp means opposite things — a renewal while billing
   * continues, a last day of access once cancelled — and printing the bare date
   * under a "Renews on" heading told an administrator that a cancelling member
   * renews. The fact belongs beside the date it qualifies rather than in the
   * status badge, which is about the account rather than about this date.
   */
  const ending = cancelAtPeriodEnd === 1;
  return (
    <span style={{ display: "block", color: ending ? "#FCD34D" : "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", whiteSpace: "nowrap" }}>
      {ending ? `Cancels on ${formatDate(currentPeriodEnd)}` : formatDate(currentPeriodEnd)}
    </span>
  );
}

/**
 * What a member is, coloured by whether anything needs doing about them.
 *
 * This replaces the Status column rather than sitting beside it. That column
 * printed a state on every row whether or not it called for action, so the row
 * that needed something looked like the eight that did not — and the badge here
 * already had a colour saying only "paid" or "not", which is the least useful
 * thing an administrator can learn from a colour.
 *
 * Green now means in good standing, and both a paid membership and a free
 * account can be in good standing — a free account has no billing to fail, so it
 * is green permanently. Amber is a problem coming, red is one that has happened.
 * The label still says which kind of account it is; the colour says whether to
 * do anything.
 *
 * Colour is never the only signal: the icon differs per level, and the tooltip
 * and accessible label carry the reason in words.
 */
function PaidStatus({ paid, attention, name, entitlementState, onOpenBilling }: { paid: boolean; attention: MemberAttention; name: string; entitlementState: AccessState | null; onOpenBilling?: () => void }) {
  const palette = {
    ok: { text: "#86EFAC", background: "rgba(34,197,94,.1)", border: "rgba(34,197,94,.3)", Icon: CheckCircle2 },
    warning: { text: "#FCD34D", background: "rgba(245,166,35,.12)", border: "rgba(245,166,35,.38)", Icon: AlertTriangle },
    critical: { text: "#FCA5A5", background: "rgba(248,113,113,.12)", border: "rgba(248,113,113,.4)", Icon: XCircle },
  }[attention.level];
  const { Icon } = palette;

  /*
   * The badge says the whole thing: what kind of account, and how it is doing.
   *
   * "Paid Member — Payment Failure" rather than a bare "Non-paid". `paidStatus`
   * is derived from the entitlement being active, so a member whose card just
   * declined falls out of "paid" and used to be labelled as though they were a
   * free account — the opposite of the action their row calls for. The summary
   * is built in shared/memberAttention.ts so this label, the tooltip and any
   * count are the same words.
   */
  const label = attention.summary;

  /*
   * An amber or red badge is a link to the thing that is wrong. Clicking it
   * opens the member on their Billing tab, where the reason and the payment
   * history are — the administrator has already said what they want to see by
   * clicking the warning, so making them find it again is a wasted step.
   *
   * A green badge stays inert. There is nothing behind it to open.
   */
  const actionable = attention.level !== "ok" && Boolean(onOpenBilling);

  return (
    <span
      className="member-attention"
      role={actionable ? "button" : undefined}
      tabIndex={actionable ? 0 : undefined}
      onClick={actionable ? event => { event.stopPropagation(); onOpenBilling?.(); } : undefined}
      onKeyDown={actionable
        ? event => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            event.stopPropagation();
            onOpenBilling?.();
          }
        : undefined}
      /*
        No native `title`. The browser's own tooltip appears after a delay,
        directly under the pointer, and sits on top of the styled one — so
        hovering produced two tooltips saying the same thing, one obscuring the
        other. The accessible name below carries the same words for assistive
        technology, which is what the attribute was really for.
      */
      aria-label={`${name}: ${label}. ${attention.summary}${attention.reasons.length ? `. ${attention.reasons.join(" ")}` : ""}`}
      style={{ display: "inline-flex", alignItems: "center", gap: ".32rem", padding: ".28rem .55rem", borderRadius: 999, background: palette.background, border: `1px solid ${palette.border}`, color: palette.text, fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".045em", whiteSpace: "nowrap", position: "relative", cursor: actionable ? "pointer" : "default" }}
    >
      <Icon size={12} aria-hidden="true" />
      {label}
      <span className="member-attention__tip" role="tooltip">
        <strong>{attention.summary}</strong>
        {attention.reasons.length > 0
          ? attention.reasons.map(reason => <span key={reason}>{reason}</span>)
          : <span>Nothing needs doing on this account.</span>}
        {attention.reasons.length > 0 && <span>Open the member to see the full record.</span>}
      </span>
    </span>
  );
}



/**
 * A member's billing record: where the membership stands, and what has happened.
 *
 * Opened from the amber or red badge on their row, so anything wrong is stated
 * first — an administrator who clicked a payment failure should not have to find
 * the failure again.
 */
function MemberBillingPanel({
  userOpenId,
  attention,
  paid,
  isSiteAdmin,
  memberSince,
  renewsAt,
  cancelAtPeriodEnd,
  subscriptionId,
  selectedInvoices,
  onSelectionChange,
}: {
  userOpenId: string;
  attention: MemberAttention | null;
  paid: boolean;
  isSiteAdmin: boolean;
  memberSince: Date | string | null;
  renewsAt: Date | string | null;
  cancelAtPeriodEnd: boolean;
  subscriptionId: string | null;
  /** Chosen invoice ids, held by the drawer so its footer can act on them. */
  selectedInvoices: string[];
  onSelectionChange: (next: string[]) => void;
}) {
  const historyQuery = trpc.members.billingHistory.useQuery({ userOpenId }, { retry: false });

  const money = (cents: number | null, currency: string | null) =>
    cents === null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: currency ?? "USD" }).format(cents / 100);

  const longDate = (value: Date | string | null) => {
    if (!value) return "—";
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime())
      ? "—"
      : new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
  };

  const daysRemaining = (() => {
    if (!renewsAt) return null;
    const date = renewsAt instanceof Date ? renewsAt : new Date(renewsAt);
    if (Number.isNaN(date.getTime())) return null;
    const days = Math.ceil((date.getTime() - Date.now()) / 86_400_000);
    return days >= 0 ? days : null;
  })();
  const realSubscriptionId = isLiveStripeSubscriptionId(subscriptionId) ? subscriptionId : null;

  /* Only entries Stripe finalised into a document can be downloaded. */
  const selectableIds = (historyQuery.data?.events ?? [])
    .filter(event => Boolean(event.invoicePdfUrl))
    .map(event => event.id);
  const someSelected = selectedInvoices.length > 0;
  const allSelectableSelected = selectableIds.length > 0 && selectableIds.every(id => selectedInvoices.includes(id));

  const tone = attention?.level === "critical"
    ? { text: "#FCA5A5", border: "rgba(248,113,113,.4)", background: "rgba(248,113,113,.1)" }
    : attention?.level === "warning"
      ? { text: "#FCD34D", border: "rgba(245,166,35,.38)", background: "rgba(245,166,35,.1)" }
      : { text: "#86EFAC", border: "rgba(34,197,94,.3)", background: "rgba(34,197,94,.1)" };

  return (
    <div className="crm-drawer__body" style={{ display: "grid", gap: "1rem", alignContent: "start" }}>
      {/* Where the membership stands, before the history of how it got there. */}
      <section className="crm-drawer-section">
        <div style={{ display: "flex", gap: "1.1rem", flexWrap: "wrap", alignItems: "flex-start" }}>
          <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto", width: 56, height: 56, borderRadius: "50%", background: "linear-gradient(145deg, rgba(233,30,140,.2), rgba(168,85,247,.18))", border: "1px solid rgba(233,30,140,.3)", color: "#F9A8D4" }}>
            <Crown size={24} />
          </span>

          <div style={{ minWidth: 0, flex: "1 1 12rem" }}>
            <span style={{ display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}>
              <strong style={{ color: "#F8FAFC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1.02rem", fontWeight: 750 }}>
                {paid ? "Paid Member" : "Free Account"}
              </strong>
              <span style={{ display: "inline-flex", padding: ".16rem .5rem", borderRadius: 999, background: tone.background, border: `1px solid ${tone.border}`, color: tone.text, fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
                {attention?.level === "critical" ? "Needs attention" : attention?.level === "warning" ? "Check this" : "Active"}
              </span>
            </span>

            {/*
              The reasons, in full, rather than a single summary line. This is the
              screen someone opens *because* something is wrong, so the whole list
              belongs here and not behind a tooltip.
            */}
            {attention && attention.reasons.length > 0 ? (
              <ul style={{ margin: ".45rem 0 0", paddingLeft: "1.05rem", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
                {attention.reasons.map(reason => <li key={reason}>{reason}</li>)}
              </ul>
            ) : (
              <p style={{ margin: ".45rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
                {paid && isSiteAdmin && !renewsAt
                  ? "Site-admin access is managed outside Stripe billing."
                  : paid ? "Membership is active and paid." : "No membership billing on this account."}
              </p>
            )}

            {typeof daysRemaining === "number" && (
              <p style={{ margin: ".3rem 0 0", color: cancelAtPeriodEnd ? "#FCD34D" : "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                {cancelAtPeriodEnd ? `Access ends in ${daysRemaining} ${daysRemaining === 1 ? "day" : "days"}` : `Renews in ${daysRemaining} ${daysRemaining === 1 ? "day" : "days"}`}
              </p>
            )}
          </div>

          <dl style={{ display: "grid", gap: ".7rem", margin: 0, paddingLeft: "1.1rem", borderLeft: "1px solid rgba(255,255,255,.08)" }}>
            <div>
              <dt style={{ display: "flex", alignItems: "center", gap: ".35rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                <CalendarDays size={13} aria-hidden="true" /> Member since
              </dt>
              <dd style={{ margin: ".1rem 0 0", color: "#F8FAFC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
                {longDate(memberSince)}
              </dd>
            </div>
            <div>
              <dt style={{ display: "flex", alignItems: "center", gap: ".35rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                <CalendarDays size={13} aria-hidden="true" /> {cancelAtPeriodEnd ? "Access ends" : "Next renewal"}
              </dt>
              <dd style={{ margin: ".1rem 0 0", color: "#F8FAFC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
                {paid ? longDate(renewsAt) : "N/A"}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {realSubscriptionId && (
        <section className="crm-drawer-section">
          <h3 className="crm-drawer-section__title">Billing reference</h3>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".65rem", padding: ".7rem .75rem", borderRadius: ".65rem", border: "1px solid rgba(125,234,255,.16)", background: "rgba(125,234,255,.045)" }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, color: "#7DEAFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".06em", textTransform: "uppercase" }}>Stripe subscription ID</p>
              <code style={{ display: "block", marginTop: ".25rem", color: "#E2E8F0", fontSize: "var(--chi-body-compact)", overflowWrap: "anywhere" }}>{realSubscriptionId}</code>
            </div>
            <button type="button" onClick={() => navigator.clipboard?.writeText(realSubscriptionId)} className="crm-drawer__secondary" style={{ flexShrink: 0 }}>Copy ID</button>
          </div>
        </section>
      )}

      <section className="crm-drawer-section">
        <h3 className="crm-drawer-section__title">Payment history</h3>
        {historyQuery.isLoading ? (
          <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>Loading…</p>
        ) : historyQuery.data?.error ? (
          <p role="alert" style={{ margin: 0, color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            The billing history could not be read. Check the Stripe connection.
          </p>
        ) : !historyQuery.data?.hasSubscription ? (
          <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
            No subscription is recorded against this member, so there is no billing history to show.
          </p>
        ) : historyQuery.data.events.length === 0 ? (
          <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            Nothing has been billed on this subscription yet.
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="member-billing-table">
              <caption className="sr-only">Payments and billing failures on this member&apos;s subscription</caption>
              <thead>
                <tr>
                  {/*
                    Select-all, in the header of the column it selects. Shows a
                    dash rather than a tick when only some rows are chosen, so
                    the box never claims more than is true.
                  */}
                  <th scope="col" style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      className="chi-checkbox"
                      aria-label="Select every invoice"
                      checked={allSelectableSelected}
                      ref={node => { if (node) node.indeterminate = someSelected && !allSelectableSelected; }}
                      onChange={event => onSelectionChange(event.target.checked ? selectableIds : [])}
                      disabled={selectableIds.length === 0}
                    />
                  </th>
                  {["Date", "Event", "Amount", "Status"].map(heading => <th key={heading} scope="col"><CrmColumnHeading label={heading} /></th>)}
                </tr>
              </thead>
              <tbody>
                {historyQuery.data.events.map(event => {
                  const failed = event.kind === "failure";
                  return (
                    <tr key={event.id}>
                      <td>
                        {/*
                          Only an invoice with a document can be downloaded. A
                          failed attempt has no PDF, so its box is disabled and
                          says why rather than selecting something that cannot be
                          fetched.
                        */}
                        <input
                          type="checkbox"
                          className="chi-checkbox"
                          aria-label={`Select the invoice from ${longDate(event.occurredAt)}`}
                          title={event.invoicePdfUrl ? undefined : "No invoice document is available for this entry"}
                          disabled={!event.invoicePdfUrl}
                          checked={selectedInvoices.includes(event.id)}
                          onChange={changeEvent => onSelectionChange(
                            changeEvent.target.checked
                              ? [...selectedInvoices, event.id]
                              : selectedInvoices.filter(id => id !== event.id),
                          )}
                        />
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>{longDate(event.occurredAt)}</td>
                      <td style={{ color: failed ? "#FCA5A5" : "#CBD5E1" }}>{event.description}</td>
                      <td style={{ whiteSpace: "nowrap", color: "#E2E8F5" }}>{money(event.amountCents, event.currency)}</td>
                      <td>
                        <span style={{ display: "inline-flex", padding: ".16rem .5rem", borderRadius: 999, background: failed ? "rgba(248,113,113,.12)" : "rgba(34,197,94,.1)", border: `1px solid ${failed ? "rgba(248,113,113,.4)" : "rgba(34,197,94,.3)"}`, color: failed ? "#FCA5A5" : "#86EFAC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
                          {failed ? "Failed" : "Paid"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="crm-drawer-section" style={{ display: "flex", gap: ".7rem", alignItems: "flex-start" }}>
        <Info size={16} aria-hidden="true" style={{ color: "#C4B5FD", flex: "0 0 auto", marginTop: ".1rem" }} />
        <div>
          <h3 className="crm-drawer-section__title" style={{ marginBottom: ".3rem" }}>About payments</h3>
          <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
            Payments are taken automatically on the renewal date. If one fails, Stripe retries and the member is notified.
            Card details are held by Stripe and are never stored or shown here.
          </p>
        </div>
      </section>
    </div>
  );
}

/**
 * This member's correspondence, and a box to add to it.
 *
 * The thread is what was actually delivered to them — campaign sends recorded
 * against their attendee profile — rather than a reconstruction from a
 * campaign's audience, so a member who was in an audience but skipped at
 * delivery does not appear to have received something they did not.
 *
 * Outbound only, and the panel says so. Nothing in the product captures a reply
 * from a member, so rendering an incoming bubble would be inventing one side of
 * a conversation. When inbound mail is stored, it slots into the same thread.
 */
function MemberMessagePanel({ userOpenId, name, email }: { userOpenId: string; name: string; email: string | null }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const historyQuery = trpc.members.messageHistory.useQuery({ userOpenId }, { retry: false });

  const messages = historyQuery.data ?? [];
  const canSend = Boolean(email) && subject.trim().length > 0 && body.trim().length > 0;

  const stamp = (iso: string | null) => {
    if (!iso) return "Queued";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "Queued";
    return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }).format(date);
  };

  return (
    <div className="crm-drawer__body" style={{ display: "grid", gap: "1rem", alignContent: "start" }}>
      <section className="crm-drawer-section">
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: ".9rem" }}>
          <div>
            <h3 className="crm-drawer-section__title" style={{ marginBottom: ".2rem" }}>Messages</h3>
            <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
              Messages sent from your organization to this member.
            </p>
          </div>
          <span style={{ color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, whiteSpace: "nowrap" }}>
            {messages.length} {messages.length === 1 ? "message" : "messages"}
          </span>
        </div>

        {historyQuery.isLoading ? (
          <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>Loading…</p>
        ) : messages.length === 0 ? (
          <p style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
            Nothing has been sent to this member yet. Anything you send below, and any campaign they are included in, appears here.
          </p>
        ) : (
          <ol style={{ display: "grid", gap: ".7rem", margin: 0, padding: 0, listStyle: "none" }}>
            {messages.map(message => (
              <li key={message.id} className="member-message">
                <span className="member-message__avatar" aria-hidden="true">CHI</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: ".5rem", flexWrap: "wrap" }}>
                    <strong style={{ color: "#F8FAFC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750 }}>
                      {message.subject}
                    </strong>
                    <span style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                      {stamp(message.occurredAt)}
                    </span>
                    {message.deliveryStatus !== "sent" && (
                      <span style={{ color: message.deliveryStatus === "failed" ? "#FCA5A5" : "#FCD34D", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
                        {message.deliveryStatus === "failed" ? "Delivery failed" : "Queued"}
                      </span>
                    )}
                  </div>
                  {message.body && (
                    <p style={{ margin: ".35rem 0 0", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                      {message.body}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="crm-drawer-section">
        <h3 className="crm-drawer-section__title" style={{ marginBottom: ".2rem" }}>Compose message</h3>
        <p style={{ margin: "0 0 .85rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
          A direct message to {name}{email ? ` at ${email}` : ""}, sent one-to-one rather than as a campaign.
        </p>

        <div style={{ marginBottom: ".7rem" }}>
          <label htmlFor="member-message-subject" style={crmLabelStyle}>Subject</label>
          <input
            id="member-message-subject"
            value={subject}
            onChange={event => setSubject(event.target.value)}
            placeholder="What the message is about"
            style={crmInputStyle}
          />
        </div>

        <div style={{ marginBottom: ".85rem" }}>
          <label htmlFor="member-message-body" style={crmLabelStyle}>Message</label>
          <textarea
            id="member-message-body"
            value={body}
            onChange={event => setBody(event.target.value)}
            placeholder="Type your message…"
            style={{ ...crmTextareaStyle, minHeight: "8rem" }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".75rem", flexWrap: "wrap" }}>
          <span style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            Sent as email from the organization.
          </span>
          <button
            type="button"
            disabled={!canSend}
            onClick={() => chiNotify.info("Direct send is not wired up yet", {
              description: "This composes the message; delivery goes through the Messages workspace so consent and delivery status stay in one place.",
            })}
            style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", minHeight: 40, padding: ".55rem 1rem", borderRadius: ".55rem", border: "none", background: "linear-gradient(135deg,#E91E8C,#C2185B)", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: canSend ? "pointer" : "not-allowed", opacity: canSend ? 1 : .55 }}
          >
            <Send size={13} /> Send message
          </button>
        </div>

        {!email && (
          <p role="alert" style={{ margin: ".7rem 0 0", color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            This member has no email address on file.
          </p>
        )}
      </section>
    </div>
  );
}

/**
 * Right-click on a member row.
 *
 * The events and speakers tables both had this and the members table did not —
 * while the panel description above it told administrators to right-click. It
 * renders the same actions the row's Actions button does, from the same array,
 * so the two cannot drift apart.
 */
function MemberRowContextMenu({ actions, subject, children }: { actions: DataTableAction[]; subject: string; children: ReactNode }) {
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="w-60">
        <ContextMenuLabel>{subject}</ContextMenuLabel>
        {actions.map((action, index) => (
          <Fragment key={action.key}>
            {/* The destructive one sits last, behind a divider, as it does in the button menu. */}
            {action.tone === "danger" && index > 0 && <ContextMenuSeparator />}
            <ContextMenuItem
              disabled={action.disabled}
              variant={action.tone === "danger" ? "destructive" : undefined}
              onSelect={action.onSelect}
            >
              {action.icon}
              {action.label}
            </ContextMenuItem>
          </Fragment>
        ))}
      </ContextMenuContent>
    </ContextMenu>
  );
}

function SortButton({ label, value, active, onSort }: { label: string; value: SortKey; active: boolean; onSort: (key: SortKey) => void }) {
  return (
    <button type="button" onClick={() => onSort(value)} style={{ display: "inline-flex", alignItems: "center", gap: ".28rem", padding: 0, border: 0, background: "transparent", font: "inherit", letterSpacing: "inherit", textTransform: "inherit", color: active ? "#F9A8D4" : "inherit", cursor: "pointer" }}>
      <CrmColumnHeading label={label} /> <ArrowUpDown size={11} />
    </button>
  );
}

type EditableField =
  | "displayName" | "professionalTitle" | "company" | "industry"
  | "location" | "bio" | "phone" | "linkedinUrl";

const EDIT_FIELDS: Array<{ key: EditableField; label: string; maxLength: number; multiline?: boolean }> = [
  { key: "displayName", label: "Full name", maxLength: 160 },
  { key: "professionalTitle", label: "Title", maxLength: 180 },
  { key: "company", label: "Company", maxLength: 180 },
  { key: "industry", label: "Industry", maxLength: 180 },
  { key: "location", label: "Location", maxLength: 180 },
  { key: "bio", label: "Bio", maxLength: 5000, multiline: true },
];

/** Example/local identifiers are intentionally never rendered as billing references. */
function isLiveStripeSubscriptionId(value: string | null | undefined): value is string {
  return Boolean(value && /^sub_[A-Za-z0-9]+$/.test(value));
}

export default function MembershipManagementView({
  onStartSpeakerProfile,
}: {
  onStartSpeakerProfile?: (userOpenId: string) => void;
}) {
  const utils = trpc.useUtils();
  const recordsQuery = trpc.members.adminRecords.useQuery(undefined, { retry: false });
  // A member's own uploaded photo is the first choice: it is the image they
  // chose for themselves and the one they see on their profile, so the CRM shows
  // the same person they do. A member who also speaks and never uploaded one
  // falls back to their published speaker portrait, matched on the speaker
  // profile's already-normalized email. Failure is tolerated: the map stays empty
  // and those rows fall back to initials.
  const speakersQuery = trpc.speakers.admin.list.useQuery(undefined, { retry: false });
  const portraitByEmail = useMemo(() => {
    const map = new Map<string, string>();
    for (const { profile } of speakersQuery.data ?? []) {
      if (!profile.publicPortraitUrl) continue;
      const key = profile.emailNormalized?.trim().toLocaleLowerCase();
      if (key) map.set(key, profile.publicPortraitUrl);
    }
    return map;
  }, [speakersQuery.data]);
  const speakerStatusByEmail = useMemo(() => {
    const map = new Map<string, "pending" | "waiting_on_account" | "approved" | "suspended">();
    for (const { profile } of speakersQuery.data ?? []) {
      const key = profile.emailNormalized?.trim().toLocaleLowerCase();
      if (key) map.set(key, profile.status);
    }
    return map;
  }, [speakersQuery.data]);
  /*
   * The renewal date Stripe holds right now.
   *
   * `memberEntitlements.currentPeriodEnd` is a mirror written by the
   * customer.subscription.* webhook, so it is only as fresh as the last webhook
   * that arrived — a missed or replayed delivery leaves the table showing a
   * stale date, or "Awaiting Stripe" for a subscription that has been billing
   * for months. This asks Stripe directly and prefers its answer, falling back
   * to the mirror when Stripe cannot be reached so the column degrades to
   * slightly-stale rather than empty.
   */
  const renewalQuery = trpc.members.memberRenewalDates.useQuery(undefined, { retry: false });
  const stripeRenewalByMember = useMemo(() => {
    const byOpenId = new Map<string, {
      renewsAt: string | null;
      subscriptionId: string | null;
      cancelAtPeriodEnd: boolean;
      paymentMethodExpiresAt?: string | null;
      missingPaymentMethod?: boolean;
    }>();
    for (const row of renewalQuery.data?.rows ?? []) {
      byOpenId.set(row.userOpenId, {
        renewsAt: row.renewsAt,
        subscriptionId: row.subscriptionId,
        cancelAtPeriodEnd: row.cancelAtPeriodEnd,
        paymentMethodExpiresAt: row.paymentMethodExpiresAt ?? null,
        missingPaymentMethod: row.missingPaymentMethod ?? false,
      });
    }
    return byOpenId;
  }, [renewalQuery.data]);

  const [search, setSearch] = useState("");
  const [addingMember, setAddingMember] = useState(false);
  /** Which section of the member drawer is showing. */
  const [memberTab, setMemberTab] = useState<"profile" | "billing" | "messages">("profile");
  /**
   * Invoices ticked in the billing tab.
   *
   * Held here rather than inside the panel because the download action lives in
   * the drawer footer, which is a sibling of the panel — and cleared whenever a
   * different member is opened, so a selection cannot follow one record onto
   * another.
   */
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);

  /** The member being given the speaker role, while the dialog asks which event. */
  const [speakerPromotion, setSpeakerPromotion] = useState<{ name: string | null; email: string | null } | null>(null);
  const [memberListView, setMemberListView] = useState<MemberListView>("current");
  const [paidFilter, setPaidFilter] = useState<PaidFilter>("all");
  /*
   * Opens sorted by the primary column, A–Z. Newest-first answers "what changed"
   * — useful once — while an administrator arriving at this table is usually
   * looking for a particular person, and alphabetical is how you find a name.
   */
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  /**
   * The member currently open for editing, held as a full draft rather than
   * per-field state so Cancel discards cleanly and the table keeps showing saved
   * values while the dialog is open.
   */
  const [editing, setEditing] = useState<null | {
    userOpenId: string;
    accessState: AccessState;
    displayName: string;
    professionalTitle: string;
    company: string;
    industry: string;
    location: string;
    bio: string;
    /** The displayed national number, e.g. "(469) 955-0269". */
    phone: string;
    phoneCountryCode: string;
    /** The username alone, shown after the fixed linkedin.com/in/ prefix. */
    linkedinUrl: string;
    interests: string[];
    /** Rows as typed; converted to stored links on save. */
    socialLinks: SocialLinkDraft[];
    /** Without the scheme, which the field shows as a fixed prefix. */
    companyWebsiteUrl: string;
    directoryVisible: boolean;
  }>(null);

  /**
   * The member's directory photo, managed from the same drawer as the rest of
   * their profile. Uses the member's own openId as the storage owner, so an
   * admin replacing a photo writes to the same object the member would.
   */
  const setMemberPhoto = trpc.members.adminSetMemberPhoto.useMutation({
    onSuccess: async () => {
      await utils.members.adminRecords.invalidate();
      setPhotoError(null);
    },
    onError: error => setPhotoError(error.message || "The photo could not be saved."),
  });
  const [photoError, setPhotoError] = useState<string | null>(null);

  async function uploadMemberPhoto(file: File, userOpenId: string) {
    setPhotoError(null);
    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      setPhotoError(`Photos must be ${MAX_IMAGE_UPLOAD_LABEL} or smaller.`);
      return;
    }

    const detectedType = await memberPhotoMimeType(file);
    if (!detectedType) {
      setPhotoError("This file is not a readable PNG, JPEG, or WebP image. Export HEIC or another format as JPEG and try again.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        setPhotoError("The photo could not be read. Try another file.");
        return;
      }
      const dataUrl = reader.result.replace(/^data:[^;,]*;base64,/i, `data:${detectedType};base64,`);
      setMemberPhoto.mutate({ userOpenId, dataUrl });
    };
    reader.onerror = () => setPhotoError("The photo could not be read. Try another file.");
    reader.readAsDataURL(file);
  }

  /** The PDFs behind the ticked rows, resolved when the download is pressed. */
  const billingHistoryForOpenMember = trpc.members.billingHistory.useQuery(
    { userOpenId: editing?.userOpenId ?? "" },
    { enabled: Boolean(editing?.userOpenId) && memberTab === "billing", retry: false },
  );

  /*
   * Opens each chosen invoice in its own tab.
   *
   * Stripe serves the PDFs, so there is nothing to bundle here — and a browser
   * blocks a burst of window.open calls it did not attribute to a click, which
   * is why they are opened synchronously from the handler rather than in a loop
   * behind an await.
   */
  function downloadSelectedInvoices() {
    const events = billingHistoryForOpenMember.data?.events ?? [];
    const urls = selectedInvoices
      .map(id => events.find(event => event.id === id)?.invoicePdfUrl)
      .filter((url): url is string => Boolean(url));

    if (urls.length === 0) {
      chiNotify.error("Nothing to download", { description: "Those entries have no invoice document in Stripe." });
      return;
    }

    for (const url of urls) window.open(url, "_blank", "noopener,noreferrer");
  }


  const updateRecord = trpc.members.updateAdminRecord.useMutation({
    onSuccess: async () => {
      await utils.members.adminRecords.invalidate();
      setEditing(null);
      chiNotify.success("Member updated", { description: "Their profile details have been saved." });
    },
    // The drawer stays open so the draft is not lost and the reason stays on screen.
    onError: error => chiNotify.error("Could not update member", { description: error.message }),
  });

  /** Row awaiting archive confirmation. Archiving is not undoable from this UI. */
  const [pendingArchive, setPendingArchive] = useState<{
    userOpenId: string;
    name: string;
    hasBillingSubscription: boolean;
  } | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const archiveMember = trpc.members.archiveMember.useMutation({
    onSuccess: async result => {
      // Update the cached list first so the member disappears from the current
      // view immediately, then refetch the server-owned status for durability.
      utils.members.adminRecords.setData(undefined, current => current?.map(record =>
        record.userOpenId === result.userOpenId
          ? { ...record, accountStatus: "archived", directoryVisible: 0 }
          : record,
      ));
      await utils.members.adminRecords.invalidate();
      setPendingArchive(null);
      setArchiveError(null);
      chiNotify.success("Member archived", {
        description: `${result.fullName} can no longer sign in. Their registrations and notifications are archived, hidden from normal views, and retained for restoration.`,
      });
    },
    onError: error => {
      // Left open so the reason stays on screen — the common refusal is an active
      // subscription, which the administrator has to go and cancel first.
      setArchiveError(error.message);
      chiNotify.error("Could not archive member", { description: error.message });
    },
  });

  const updateAccess = trpc.members.updateAccessState.useMutation({
    onSuccess: async () => {
      await utils.members.adminRecords.invalidate();
      chiNotify.success("Membership status saved", { description: "The registered user's access state has been updated." });
    },
    onError: error => chiNotify.error("Could not update membership", { description: error.message }),
  });

  /**
   * Opens the edit drawer for a member.
   *
   * Extracted because the row itself opens it now as well as the menu item, and
   * two copies of this prefill would be two places for the phone and LinkedIn
   * conversions to drift apart.
   */
  /*
   * `startOn` exists so the badge on a row can open the record where the problem
   * is. Clicking an amber or red status and landing on the profile form would
   * make the administrator navigate to the billing tab themselves, having
   * already said what they wanted to see.
   */
  function openMemberEditor(record: (typeof rows)[number], startOn: "profile" | "billing" | "messages" = "profile") {
    const phone = splitStoredPhone(record.phone);
    setMemberTab(startOn);
    setSelectedInvoices([]);
    setEditing({
      userOpenId: record.userOpenId,
      // accessState is required by the endpoint, so it is carried through the
      // drawer unchanged rather than defaulted — a default would silently reset
      // the member's access.
      accessState: record.accessState,
      displayName: record.name ?? "",
      professionalTitle: record.professionalTitle ?? "",
      company: record.company ?? "",
      industry: record.industry ?? "",
      location: record.location ?? "",
      bio: record.bio ?? "",
      phone: phone.national,
      phoneCountryCode: phone.countryCode,
      linkedinUrl: linkedinUsernameFromStored(record.linkedinUrl),
      interests: record.interests ?? [],
      socialLinks: socialLinkDrafts(parseSocialLinks(record.socialLinksJson)),
      companyWebsiteUrl: (record.companyWebsiteUrl ?? "").replace(/^https:\/\//, ""),
      directoryVisible: record.directoryVisible === 1,
    });
  }


  /*
   * What can be done to one member. Built once per row and handed to both the
   * Actions button and the right-click menu, so the two can never offer
   * different things.
   */
  function memberRowActions(record: (typeof rows)[number]): DataTableAction[] {
    const emailKey = record.email?.trim().toLocaleLowerCase() ?? "";
    const speakerStatus = speakerStatusByEmail.get(emailKey);
    return [
                          {
                            key: "edit",
                            label: "Edit member",
                            icon: <Pencil size={13} aria-hidden="true" />,
                            onSelect: () => openMemberEditor(record),
                          },
                          ...(record.accessState === "active"
                            ? [{
                                key: "deactivate",
                                label: "Set membership inactive",
                                icon: <XCircle size={13} aria-hidden="true" />,
                                disabled: updateAccess.isPending,
                                onSelect: () => updateAccess.mutate({ userOpenId: record.userOpenId, accessState: "inactive" as AccessState }),
                              }]
                            : [{
                                key: "activate",
                                label: "Set membership active",
                                icon: <CheckCircle2 size={13} aria-hidden="true" />,
                                disabled: updateAccess.isPending,
                                onSelect: () => updateAccess.mutate({ userOpenId: record.userOpenId, accessState: "active" as AccessState }),
                              }]),
                          /*
                            Two different jobs behind one former label. An
                            existing speaker is managed on the speakers page, so
                            that item navigates. A member who is not a speaker yet
                            needs the role granted — which is a write, and used to
                            navigate to a page that had nothing waiting for it, so
                            the administrator landed on a roster that did not
                            contain the person they had just tried to add.
                          */
                          ...(speakerStatus
                            ? (onStartSpeakerProfile
                                ? [{
                                    key: "speaker",
                                    label: "Manage speaker profile",
                                    icon: <Users size={13} aria-hidden="true" />,
                                    title: `Manage this member's ${speakerStatus} speaker profile`,
                                    onSelect: () => onStartSpeakerProfile(record.userOpenId),
                                  }]
                                : [])
                            : [{
                                key: "speaker",
                                label: "Add speaker to event",
                                icon: <Mic2 size={13} aria-hidden="true" />,
                                disabled: !record.email,
                                title: record.email
                                  ? "Grant the speaker role, assign an event, and send the congratulations email"
                                  : "This member has no email address, which a speaker record is keyed on",
                                onSelect: () => setSpeakerPromotion({ name: record.name, email: record.email }),
                              }]),
                          {
                            key: "archive",
                            label: "Archive member",
                            icon: <Archive size={13} aria-hidden="true" />,
                            tone: "danger" as const,
                            disabled: archiveMember.isPending,
                            title: "Archive this member — they lose sign-in and directory access after any billing subscription is cancelled",
                            onSelect: () => {
                              setArchiveError(null);
                              setPendingArchive({
                                userOpenId: record.userOpenId,
                                name: record.name ?? record.email ?? "this member",
                                hasBillingSubscription: record.entitlementState === "active" || record.entitlementState === "past_due",
                              });
                            },
                          },
                        ];
  }

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return filterMemberRecordsByListView(recordsQuery.data ?? [], memberListView)
      .filter(record => paidFilter === "all" || record.paidStatus === paidFilter)
      .filter(record => {
        if (!needle) return true;
        return [record.name, record.email, record.company, record.professionalTitle]
          .some(value => value?.toLowerCase().includes(needle));
      })
      .sort((left, right) => {
        const leftValue = sortKey === "name"
          ? left.name ?? left.email ?? ""
          : sortKey === "paidStatus"
            ? left.paidStatus
            : new Date(left[sortKey] ?? 0).getTime();
        const rightValue = sortKey === "name"
          ? right.name ?? right.email ?? ""
          : sortKey === "paidStatus"
            ? right.paidStatus
            : new Date(right[sortKey] ?? 0).getTime();
        const comparison = typeof leftValue === "number"
          ? leftValue - (rightValue as number)
          : leftValue.localeCompare(rightValue as string);
        return sortDirection === "asc" ? comparison : -comparison;
      });
  }, [memberListView, paidFilter, recordsQuery.data, search, sortDirection, sortKey]);

  /*
   * The open member's paid state and attention, resolved once for the drawer.
   * The tabs need to know whether to offer Billing, and the billing panel needs
   * the same reasons the row badge showed — computed from the same inputs so the
   * two can never disagree.
   */
  const editingRecord = editing ? rows.find(row => row.userOpenId === editing.userOpenId) ?? null : null;
  const editingIsPaid = editingRecord
    ? editingRecord.paidStatus === "paid" || editingRecord.entitlementState === "past_due"
    : false;
  const editingAttention = editingRecord
    ? (() => {
        const live = stripeRenewalByMember.get(editingRecord.userOpenId);
        return memberAttention({
          entitlementState: editingRecord.entitlementState,
          paidStatus: editingRecord.paidStatus,
          isSiteAdmin: editingRecord.role === "admin",
          currentPeriodEnd: live?.renewsAt ?? editingRecord.currentPeriodEnd,
          cancelAtPeriodEnd: live ? live.cancelAtPeriodEnd : editingRecord.cancelAtPeriodEnd === 1,
          paymentMethodExpiresAt: live?.paymentMethodExpiresAt ?? null,
          missingPaymentMethod: live?.missingPaymentMethod ?? false,
        });
      })()
    : null;


  const totals = useMemo(() => {
    const all = recordsQuery.data ?? [];
    const current = filterMemberRecordsByListView(all, "current");
    const archived = filterMemberRecordsByListView(all, "archived");
    return {
      current: current.length,
      archived: archived.length,
      paid: current.filter(record => record.paidStatus === "paid").length,
      nonPaid: current.filter(record => record.paidStatus === "non_paid").length,
    };
  }, [recordsQuery.data]);

  const changeSort = (next: SortKey) => {
    if (next === sortKey) {
      setSortDirection(direction => direction === "asc" ? "desc" : "asc");
      return;
    }
    setSortKey(next);
    setSortDirection(next === "name" ? "asc" : "desc");
  };

  if (recordsQuery.isLoading) {
    return <div style={{ flex: 1, display: "grid", placeItems: "center", color: "#94A3B8" }}><Loader2 className="animate-spin" aria-label="Loading registered users" /></div>;
  }

  if (recordsQuery.isError) {
    return (
      <div role="alert" style={{ flex: 1, display: "grid", placeItems: "center", padding: "2rem", color: "#FECACA", textAlign: "center" }}>
        <div style={{ maxWidth: 440, padding: "1.35rem", borderRadius: ".9rem", background: "rgba(244,63,94,.08)", border: "1px solid rgba(244,63,94,.25)" }}>
          <XCircle size={24} style={{ margin: "0 auto .65rem", color: "#FDA4AF" }} />
          <strong style={{ display: "block", fontFamily: "'Space Grotesk', sans-serif" }}>Registered users could not be loaded</strong>
          <button type="button" onClick={() => recordsQuery.refetch()} style={{ marginTop: ".9rem", minHeight: 38, padding: "0 .85rem", border: 0, borderRadius: ".5rem", background: "#BE185D", color: "white", fontWeight: 700, cursor: "pointer" }}><RefreshCw size={13} style={{ display: "inline", marginRight: 6 }} />Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "1.5rem clamp(1rem,3vw,2.4rem) 3rem", color: "white" }}>
      <style>{`
        /*
          Square at the top, rounded at the bottom.

          The table sits directly under the panel's search row inside one
          container, so a rounded top edge drew a second corner a few pixels
          below the panel's own — two nested roundings reading as a card inside a
          card. The bottom is the panel's real edge and keeps its curve.
        */
        .membership-table-wrap { overflow-x:auto; border:1px solid rgba(255,255,255,.08); border-top:0; border-radius:0 0 .9rem .9rem; background:var(--chi-table-surface); }
        .membership-table { width:100%; min-width:1130px; border-collapse:collapse; }
        .membership-table th,.membership-table td { padding:.8rem .85rem; border-bottom:1px solid rgba(255,255,255,.065); text-align:left; vertical-align:middle; }
        /* Sized to the portrait and no wider: a table's leading column will
           otherwise take a share of the free space that a fixed-size avatar has no
           use for, pushing every heading away from the data beneath it.

           38px to match the portrait's current size, and a real gap to its right.
           This used to force padding-right:0 against a 30px box, so the face sat
           hard against the name beside it with the two reading as one smudged
           column — and the hover scale made them briefly overlap. */
        .crm-member-portrait-cell { width:46px; padding-right:1.1rem !important; }
        /* Sortable and plain headers were different sizes, so the ones you could
           sort by looked like smaller print than the ones you could not. */
        .membership-table th { color:#7E8CA0; font-family:'Space Grotesk',sans-serif; font-size:var(--chi-body-compact); font-weight:750; letter-spacing:.07em; text-transform:uppercase; }
        .membership-table tbody tr:last-child td { border-bottom:0; }
        .membership-table tbody tr:hover { background:rgba(255,255,255,.025); }
        .membership-table__row { cursor:pointer; }
        .membership-table__row:focus-visible { outline:2px solid #F472B6; outline-offset:-2px; }
        @media (max-width:720px) { .membership-table-wrap { margin-inline:-.25rem; border-radius:0 0 .7rem .7rem; } }
      `}</style>

      <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "1.15rem" }}>
        <div>
          <p style={{ margin: 0, color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".12em", textTransform: "uppercase" }}>Registered site users</p>
          <h1 style={{ margin: ".28rem 0 0", fontFamily: "'Bebas Neue', sans-serif", fontSize: "2.25rem", letterSpacing: ".025em" }}>Members</h1>
          <p style={{ margin: ".3rem 0 0", maxWidth: 620, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>{memberListView === "current" ? "Current accounts remain visible here until they are archived. Paid status comes from the current membership entitlement." : "Archived accounts are kept for registration and payment history, but no longer have sign-in or directory access."}</p>
        </div>
        {/*
          Its own line, and the same size as every other CRM primary action.
          Sharing a row with the count tiles made it read as a fifth statistic,
          and it was a different height from the Create speaker button one screen
          across.
        */}
        <div style={{ display: "flex", width: "100%" }}>
          <button
            type="button"
            onClick={() => setAddingMember(true)}
            style={{ minHeight: 44, display: "inline-flex", alignItems: "center", gap: ".5rem", padding: ".7rem .9rem", border: "1px solid rgba(233,30,140,.45)", borderRadius: ".65rem", background: "linear-gradient(135deg,#E91E8C,#C2185B)", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: "pointer" }}
          >
            <UserPlus size={16} /> Add member
          </button>
        </div>
      </header>

      {addingMember && (
        <AddMemberDrawer
          onClose={() => setAddingMember(false)}
          onCreated={() => recordsQuery.refetch()}
        />
      )}

      {speakerPromotion && (
        <AddSpeakerRoleDialog
          member={speakerPromotion}
          onClose={() => setSpeakerPromotion(null)}
          onPromoted={() => recordsQuery.refetch()}
        />
      )}

      {/*
        The same filter tiles the Payments view uses, on the same principle: the
        count and the control that acts on it are one thing, so a number on screen
        is always clickable rather than being a statistic beside a separate pill
        that repeats it. Those two used to disagree — the tiles sat top-right and
        the filters sat above the table, and only one of them showed a selection.

        Two axes on one row. Current and Archived choose which population is
        listed; Paid and Non-paid narrow whichever population that is. So Paid
        deliberately does not leave the archived list when you are in it — an
        archived member who paid is a real thing to want to see.
      */}
      <CrmFilterTiles
        ariaLabel="Member filters"
        tiles={[
          { key: "current", label: "Current", value: totals.current, icon: Users, color: "#67E8F9", background: "rgba(0,212,255,.10)", border: "rgba(0,212,255,.22)",
            selected: memberListView === "current" && paidFilter === "all",
            onSelect: () => { setMemberListView("current"); setPaidFilter("all"); } },
          { key: "archived", label: "Archived", value: totals.archived, icon: Archive, color: "#FBBF24", background: "rgba(245,166,35,.12)", border: "rgba(245,166,35,.26)",
            selected: memberListView === "archived" && paidFilter === "all",
            onSelect: () => { setMemberListView("archived"); setPaidFilter("all"); } },
          { key: "paid", label: "Paid", value: totals.paid, icon: CheckCircle2, color: "#86EFAC", background: "rgba(34,197,94,.12)", border: "rgba(34,197,94,.28)",
            selected: paidFilter === "paid",
            onSelect: () => setPaidFilter(paidFilter === "paid" ? "all" : "paid") },
          { key: "non_paid", label: "Non-paid", value: totals.nonPaid, icon: XCircle, color: "#CBD5E1", background: "rgba(148,163,184,.12)", border: "rgba(148,163,184,.26)",
            selected: paidFilter === "non_paid",
            onSelect: () => setPaidFilter(paidFilter === "non_paid" ? "all" : "non_paid") },
        ]}
      />

      {paidFilter !== "all" && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: ".85rem" }}>
          <button type="button" onClick={() => setPaidFilter("all")} style={{ minHeight: 40, padding: ".45rem .8rem", borderRadius: 999, border: "1px solid rgba(233,30,140,.6)", background: "rgba(233,30,140,.14)", color: "#FBCFE8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}>
            Clear {paidFilter === "paid" ? "paid" : "non-paid"} filter
          </button>
        </div>
      )}

      <CrmTablePanel
        title={memberListView === "archived" ? "Archived members" : "Member roster"}
        description="Click a row to edit the member, right-click for actions, or use the Actions button. Contact details are visible only to administrators."
        icon={Users}
        search={search}
        onSearchChange={setSearch}
        searchLabel={`Search ${memberListView} members`}
        searchPlaceholder="Search name, email, company or title…"
        shownCount={rows.length}
        totalCount={memberListView === "archived" ? totals.archived : totals.current}
        noun="member"
        emptyState={
          <div style={{ padding: "2.5rem 1.25rem", textAlign: "center" }}>
            <Users size={30} style={{ color: "#475569", marginBottom: ".8rem" }} />
            <p style={{ margin: 0, color: "#CCD6E5", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 650 }}>
              {memberListView === "archived" ? "No archived members" : "No members yet"}
            </p>
            <p style={{ margin: ".4rem 0 0", color: "#728096", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
              {memberListView === "archived"
                ? "Archiving a member keeps their registration and payment history while removing sign-in and directory access."
                : "Add a member, or wait for someone to create an account from the public site."}
            </p>
          </div>
        }
      >
      <div className="membership-table-wrap">
        <table className="membership-table" aria-label="Registered site users">
          <thead>
            <tr>
              {/* Unlabelled: a face needs no heading, and naming it would compete with Name. */}
              <th className="crm-member-portrait-cell"><span className="sr-only">Photo</span></th>
              <th><SortButton label="Name" value="name" active={sortKey === "name"} onSort={changeSort} /></th>
              <th><CrmColumnHeading label="Contact" /></th>
              <th><CrmColumnHeading label="Company / title" /></th>
              <th><SortButton label="Member" value="paidStatus" active={sortKey === "paidStatus"} onSort={changeSort} /></th>
              <th><CrmColumnHeading label="Renews on" /></th>
              <th><SortButton label="Created" value="createdAt" active={sortKey === "createdAt"} onSort={changeSort} /></th>
              <th><CrmColumnHeading label="Actions" /></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(record => {
              const isArchived = record.accountStatus === "archived";
              const emailKey = record.email?.trim().toLocaleLowerCase() ?? "";
              const speakerStatus = speakerStatusByEmail.get(emailKey);
              /*
                One list, two triggers.
                
                The Actions button and the right-click menu offer the same things
                on the same row, so they are built from the same array. They were
                not — right-click did nothing at all here, while the events and
                speakers tables both had it, and the panel description above this
                table told administrators to use it.
              */
              const rowActions: DataTableAction[] = memberRowActions(record);
              return (
                /* The key belongs on the outermost element of the mapped item,
                   which is the wrapper now rather than the row inside it. */
                <MemberRowContextMenu key={record.userOpenId} actions={rowActions} subject={record.name ?? record.email ?? "member"}>
                <tr
                  /*
                    The row opens the member, the way the events and speakers
                    tables open theirs. Not a link: the row contains its own
                    buttons, and nesting them in an anchor would put them out of
                    reach of a keyboard.
                  */
                  tabIndex={0}
                  role="button"
                  aria-label={`Edit ${record.name ?? record.email ?? "member"}`}
                  className="membership-table__row"
                  onClick={() => openMemberEditor(record)}
                  onKeyDown={event => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    // Space scrolls the page unless it is claimed here.
                    event.preventDefault();
                    openMemberEditor(record);
                  }}
                >
                  {/*
                    The portrait's own cell, under the table's unlabelled first
                    heading. Separating it from the name is what lets every other
                    column sit under the heading that names it, instead of the
                    whole table being shifted right by a face inside cell one.
                  */}
                  <td className="crm-member-portrait-cell">
                    <span className="crm-member-portrait">
                      <MemberProfilePhoto
                        name={record.name ?? record.email ?? "Member"}
                        memberPhotoUrl={record.photoUrl}
                        speakerPortraitUrl={record.speakerPortraitUrl || portraitByEmail.get(emailKey)}
                        size={CRM_ROW_PORTRAIT_SIZE}
                        accent="#E91E8C"
                      />
                    </span>
                  </td>
                  <td>
                    {/*
                      "Community account" used to sit under every name, which is the
                      default and so said nothing; only the legacy exception is
                      worth a label.
                    */}
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ display: "block", color: "#F8FAFC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>{record.name ?? "Unnamed account"}</strong>
                      {record.source !== "community" && (
                        <span style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>Legacy account</span>
                      )}
                    </div>
                  </td>
                  <td><span style={{ color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{record.email ?? "No email"}</span>{record.phone && <span style={{ display: "block", marginTop: ".16rem", color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{formatPhoneForDisplay(record.phone)}</span>}</td>
                  <td><span style={{ display: "block", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{record.company ?? "—"}</span><span style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{record.professionalTitle ?? "No title"}</span></td>
                  <td>{(() => {
                    const live = stripeRenewalByMember.get(record.userOpenId);
                    return (
                      <PaidStatus
                        paid={record.paidStatus === "paid"}
                        entitlementState={record.entitlementState}
                        name={record.name ?? record.email ?? "this member"}
                        onOpenBilling={() => openMemberEditor(record, "billing")}
                        attention={memberAttention({
                          entitlementState: record.entitlementState,
                          paidStatus: record.paidStatus,
                          isSiteAdmin: record.role === "admin",
                          currentPeriodEnd: live?.renewsAt ?? record.currentPeriodEnd,
                          cancelAtPeriodEnd: live ? live.cancelAtPeriodEnd : record.cancelAtPeriodEnd === 1,
                          paymentMethodExpiresAt: live?.paymentMethodExpiresAt ?? null,
                          missingPaymentMethod: live?.missingPaymentMethod ?? false,
                        })}
                      />
                    );
                  })()}</td>
                  <td>{(() => {
                    const live = stripeRenewalByMember.get(record.userOpenId);
                    return (
                      <RenewalDate
                        paid={record.paidStatus === "paid"}
                        currentPeriodEnd={live?.renewsAt ?? record.currentPeriodEnd}
                        cancelAtPeriodEnd={live ? (live.cancelAtPeriodEnd ? 1 : 0) : record.cancelAtPeriodEnd}
                        pending={renewalQuery.isLoading}
                      />
                    );
                  })()}</td>
                  <td><span style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{formatDate(record.createdAt)}</span></td>
                  <td
                    /* The menu lives inside a row that opens the member, so its
                       clicks and keys stop here rather than doing both. */
                    onClick={event => event.stopPropagation()}
                    onKeyDown={event => event.stopPropagation()}
                  >
                    {isArchived ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: ".3rem", color: "#FBBF24", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}><Archive size={12} /> Archived account</span>
                    ) : (
                      /*
                        One trigger per row. The access state is in here too rather
                        than as a loose select beside it: it is one of the actions
                        an administrator takes on a member, and leaving it outside
                        put a form control in a column of buttons.
                      */
                      <DataTableActions
                        subject={record.name ?? record.email ?? "member"}
                        actions={rowActions}
                      />
                    )}
                  </td>
                </tr>
                </MemberRowContextMenu>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && <div style={{ padding: "2.25rem 1rem", textAlign: "center", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}><Users size={21} style={{ margin: "0 auto .55rem", color: "#64748B" }} />No {memberListView === "archived" ? "archived" : "current"} members match this view.</div>}
      </div>
      </CrmTablePanel>

      {/*
        Confirmation, because this is not undoable from here — restoring a member
        means a database change. States plainly what is kept and what is lost, so
        the decision is made with both in view rather than from the word "archive".
      */}
      {/*
        Profile details only. Membership access stays on the row's own select so
        there is exactly one control for it — two would let the dialog silently
        revert an access change made in the row behind it.
      */}
      {/*
        A right-side drawer rather than a centred dialog, matching the speaker
        drawers and the member-facing ones: this form is a column of fields, and
        a drawer gives it the height to breathe without the page jumping behind it.
      */}
      <Sheet open={Boolean(editing)} onOpenChange={open => { if (!open) setEditing(null); }}>
        <SheetContent side="right" className="speaker-create-drawer">
          <form
            className="crm-drawer"
            onSubmit={event => {
              event.preventDefault();
              if (!editing) return;
              const { phoneCountryCode, ...draft } = editing;
              try {
                const { socialLinks, companyWebsiteUrl, ...rest } = draft;
                updateRecord.mutate({
                  ...rest,
                  phone: storedPhoneValue(phoneCountryCode, editing.phone) ?? "",
                  linkedinUrl: storedLinkedInValue(editing.linkedinUrl) ?? "",
                  // Sent as typed. The server normalizes through the same shared
                  // rules the form validated against, so it is the one authority
                  // on what actually gets stored.
                  socialLinks: socialLinks.map(link => ({ platform: link.platform, url: link.value })),
                  companyWebsiteUrl: companyWebsiteUrl.trim(),
                });
              } catch (error) {
                // normalizeLinkedInProfile throws on a malformed username rather
                // than silently storing something that is not a profile URL.
                setPhotoError(error instanceof Error ? error.message : "Check the LinkedIn username.");
              }
            }}
          >
            <SheetHeader className="crm-drawer__header">
              <SheetTitle className="crm-drawer__title">Edit member</SheetTitle>
              {/*
                Tabs, because this drawer now holds three unrelated jobs: the
                profile fields, the billing record, and sending the member a
                message. Stacked in one scroll they buried each other, and the
                billing history — the part someone opens this drawer *for* when a
                payment fails — sat below a long form they had no interest in.

                Billing appears only for a paid member. There is no billing record
                behind a free account, and an empty tab that can never fill is a
                dead end rather than information.
              */}
              <div role="tablist" aria-label="Member record sections" className="member-drawer-tabs">
                {([
                  { key: "profile" as const, label: "Edit Information", flags: 0 },
                  ...(editingIsPaid ? [{ key: "billing" as const, label: "Billing", flags: editingAttention?.reasons.length ?? 0 }] : []),
                  { key: "messages" as const, label: "Messages", flags: 0 },
                ]).map(tab => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={memberTab === tab.key}
                    onClick={() => setMemberTab(tab.key)}
                    data-active={memberTab === tab.key || undefined}
                  >
                    {tab.label}
                    {/*
                      The count of things wrong with this record. Red, round, and
                      carrying a number rather than a dot, because "two problems"
                      and "one problem" are different amounts of work — and it is
                      labelled in words as well, so the meaning does not rest on
                      the colour alone.
                    */}
                    {tab.flags > 0 && (
                      <span className="member-drawer-tabs__flag" aria-label={`${tab.flags} ${tab.flags === 1 ? "issue" : "issues"} on this account`}>
                        {tab.flags}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </SheetHeader>
            <div className="crm-drawer__body" style={{ display: memberTab === "profile" ? "grid" : "none", gap: ".7rem" }}>
              {/*
                The portrait, editable here rather than only by the member. An
                admin could always see a wrong or missing photo in the roster and
                never fix it without asking the member to sign in.
              */}
              {(() => {
                const record = rows.find(row => row.userOpenId === editing?.userOpenId);
                /*
                 * Resolved exactly as the table row resolves it, including the
                 * speaker-portrait fallback. Reading only photoUrl meant a member
                 * whose picture comes from their speaker portrait showed a face in
                 * the roster and initials in the drawer that edits it.
                 */
                const recordEmailKey = record?.email?.trim().toLocaleLowerCase() ?? "";
                const memberPhoto = record?.photoUrl;
                const speakerPhoto = record?.speakerPortraitUrl || portraitByEmail.get(recordEmailKey);
                const importedPhoto = importedMemberPhotoUrl(editing?.displayName || record?.name || "");
                const hasAnyPhoto = Boolean(memberPhoto || importedPhoto || speakerPhoto);
                const busy = setMemberPhoto.isPending;
                return (
                  /*
                    Who this is, before the fields that edit them.

                    The drawer opened straight into a photo control and a column
                    of inputs, so the only way to confirm whose record was open
                    was to read the name field. Identity belongs at the top,
                    stated rather than editable, with the photo action to the
                    right where it does not interrupt it.
                  */
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem", paddingBottom: "1rem", borderBottom: "1px solid rgba(255,255,255,.08)", flexWrap: "wrap" }}>
                    <MemberProfilePhoto
                      name={editing?.displayName || record?.email || "Member"}
                      memberPhotoUrl={memberPhoto}
                      speakerPortraitUrl={speakerPhoto}
                      size={64}
                      accent="#E91E8C"
                    />
                    <div style={{ display: "grid", gap: ".25rem", minWidth: 0, flex: "1 1 12rem" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}>
                        <strong style={{ color: "#F8FAFC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1.02rem", fontWeight: 750 }}>
                          {editing?.displayName || "Unnamed member"}
                        </strong>
                        {editingAttention && (
                          <span style={{ display: "inline-flex", alignItems: "center", padding: ".18rem .5rem", borderRadius: 999, background: "rgba(168,85,247,.14)", border: "1px solid rgba(168,85,247,.32)", color: "#D8B4FE", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, whiteSpace: "nowrap" }}>
                            {editingAttention.summary}
                          </span>
                        )}
                      </span>
                      {record?.email && <span style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{record.email}</span>}
                      {record?.phone && <span style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{formatPhoneForDisplay(record.phone)}</span>}
                    </div>
                    <div style={{ display: "grid", gap: ".4rem", justifyItems: "end", marginLeft: "auto" }}>
                      <span style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                        {memberPhoto ? "Member Profile Picture" : importedPhoto ? "Imported photo" : hasAnyPhoto ? "Fallback photo" : "No photo uploaded"}
                      </span>
                      <div style={{ display: "flex", gap: ".45rem", flexWrap: "wrap" }}>
                        <label className="crm-drawer__secondary" style={{ display: "inline-flex", alignItems: "center", minHeight: 36, padding: "0 .8rem", cursor: busy ? "wait" : "pointer" }}>
                          {busy ? "Uploading…" : memberPhoto ? "Replace photo" : "Upload photo"}
                          <input
                            type="file"
                            accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                            disabled={busy}
                            style={{ display: "none" }}
                            onChange={event => {
                              const file = event.target.files?.[0];
                              event.target.value = "";
                              if (!file || !editing) return;
                               void uploadMemberPhoto(file, editing.userOpenId);
                            }}
                          />
                        </label>
                        {memberPhoto && (
                          <button
                            type="button"
                            className="crm-drawer__secondary"
                            disabled={busy}
                            style={{ minHeight: 36, padding: "0 .8rem" }}
                            onClick={() => editing && setMemberPhoto.mutate({ userOpenId: editing.userOpenId, dataUrl: null })}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      {photoError && <span role="alert" style={{ color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{photoError}</span>}
                    </div>
                  </div>
                );
              })()}

            {/*
              The event drawer's field styling, imported rather than restated, so
              the two admin forms read as one product. See crmFormStyles.ts.
            */}
            {/*
              Grouped under headings rather than run as one column. "Bio" and
              "Phone" answer different questions about a person, and a flat list
              made every field equally prominent — so finding one meant reading
              all of them. Two-up where a field is short, full width where it is
              not.
            */}
            <section className="crm-drawer-section">
              <h3 className="crm-drawer-section__title">Personal &amp; professional</h3>
              <div className="crm-drawer-grid">
            {EDIT_FIELDS.map(field => (
              /*
                The assistant is a sibling of the label, never inside it. A label
                may bind to one control; nesting the assistant's own notes textarea
                within it left "Bio" pointing at nothing in particular, which breaks
                the field for a screen reader as surely as it broke the test that
                looked the field up by name.
              */
              <div key={field.key} className={field.multiline || field.key === "location" ? "crm-drawer-grid--full" : undefined}>
              <label style={{ display: "block" }}>
                <span style={crmLabelStyle}>{field.label}</span>
                {field.multiline ? (
                  <textarea
                    value={editing?.[field.key] ?? ""}
                    rows={4}
                    maxLength={field.maxLength}
                    onChange={e => setEditing(current => current && { ...current, [field.key]: e.target.value })}
                    style={crmTextareaStyle}
                  />
                ) : (
                  <input
                    type="text"
                    value={editing?.[field.key] ?? ""}
                    maxLength={field.maxLength}
                    onChange={e => setEditing(current => current && { ...current, [field.key]: e.target.value })}
                    style={crmInputStyle}
                  />
                )}
              </label>
              {/*
                Only the biography gets the assistant: it is the one field here that
                is prose someone has to compose rather than a fact they look up.
              */}
              {field.key === "bio" && (
                <BiographyAssistant
                  value={editing?.bio ?? ""}
                  maxLength={field.maxLength}
                  onApprove={revised => setEditing(current => current && { ...current, bio: revised })}
                />
              )}
              </div>
            ))}
              </div>
            </section>

            <section className="crm-drawer-section">
              <h3 className="crm-drawer-section__title">Contact details</h3>
            {/*
              The same phone and LinkedIn controls the create-account flow uses,
              so a number formats as it is typed and a LinkedIn profile is entered
              as a username after a fixed prefix — rather than each form inventing
              its own rules for the same two fields.
            */}
            <div>
              <label htmlFor="member-edit-phone" style={crmLabelStyle}>Phone</label>
              <PhoneInput
                id="member-edit-phone"
                label="Phone"
                countryCode={editing?.phoneCountryCode ?? "+1"}
                value={editing?.phone ?? ""}
                onChange={next => setEditing(current => current && { ...current, phoneCountryCode: next.countryCode, phone: next.value })}
              />
            </div>

            <div>
              <label htmlFor="member-edit-linkedin" style={crmLabelStyle}>LinkedIn</label>
              <LinkedInInput
                id="member-edit-linkedin"
                value={editing?.linkedinUrl ?? ""}
                onChange={username => setEditing(current => current && { ...current, linkedinUrl: username })}
              />
              <span style={crmHelperTextStyle}>{LINKEDIN_INPUT_HINT}</span>
            </div>

            <div>
              <span style={crmLabelStyle}>Company website</span>
              <CompanyWebsiteInput
                id="member-edit-company-website"
                value={editing?.companyWebsiteUrl ?? ""}
                onChange={next => setEditing(current => current && { ...current, companyWebsiteUrl: next })}
              />
            </div>
            </section>

            <section className="crm-drawer-section">
              <h3 className="crm-drawer-section__title">Links &amp; visibility</h3>
            <div>
              <span style={crmLabelStyle}>Social links</span>
              <SocialLinksInput
                idPrefix="member-edit-social"
                drafts={editing?.socialLinks ?? []}
                onChange={next => setEditing(current => current && { ...current, socialLinks: next })}
              />
            </div>

            <div>
              <span style={crmLabelStyle}>Interests</span>
              <InterestTypeahead
                options={MEMBER_INTERESTS}
                selected={editing?.interests ?? []}
                onToggle={value => setEditing(current => current && {
                  ...current,
                  interests: current.interests.includes(value)
                    ? current.interests.filter(entry => entry !== value)
                    : [...current.interests, value],
                })}
              />
            </div>
            <div style={{ marginTop: ".3rem", paddingTop: ".7rem", borderTop: "1px solid rgba(255,255,255,.08)" }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: ".55rem", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={editing?.directoryVisible ?? false}
                  onChange={e => setEditing(current => current && { ...current, directoryVisible: e.target.checked })}
                  style={{ marginTop: ".15rem", width: 15, height: 15, accentColor: "#0E7490", cursor: "pointer" }}
                />
                <span style={{ display: "grid", gap: ".15rem" }}>
                  <span style={{ fontSize: "var(--chi-body-compact)", fontWeight: 700, color: "#E2E8F5" }}>List this member in the directory</span>
                  <span style={{ fontSize: "var(--chi-body-compact)", lineHeight: 1.45, color: "#94A3B8" }}>
                    Turning this off removes them from the member directory without
                    affecting their account or history. Turning it on publishes the
                    fields they have chosen to share — it does not override those
                    individual choices.
                  </span>
                </span>
              </label>
            </div>
            </section>
            </div>

            {memberTab === "billing" && editing && (
              <MemberBillingPanel
                userOpenId={editing.userOpenId}
                attention={editingAttention}
                paid={editingIsPaid}
                isSiteAdmin={editingRecord?.role === "admin"}
                memberSince={editingRecord?.createdAt ?? null}
                renewsAt={stripeRenewalByMember.get(editing.userOpenId)?.renewsAt ?? editingRecord?.currentPeriodEnd ?? null}
                subscriptionId={stripeRenewalByMember.get(editing.userOpenId)?.subscriptionId ?? null}
                cancelAtPeriodEnd={
                  stripeRenewalByMember.get(editing.userOpenId)?.cancelAtPeriodEnd
                  ?? editingRecord?.cancelAtPeriodEnd === 1
                }
                selectedInvoices={selectedInvoices}
                onSelectionChange={setSelectedInvoices}
              />
            )}

            {memberTab === "messages" && editing && (
              <MemberMessagePanel
                userOpenId={editing.userOpenId}
                name={editing.displayName || editing.userOpenId}
                email={rows.find(row => row.userOpenId === editing.userOpenId)?.email ?? null}
              />
            )}

            {/*
              Only the profile form saves. The other tabs act on their own — a
              billing history has nothing to save, and a message sends itself — so
              showing "Save changes" beneath them would offer to save something
              that was never edited.
            */}
            {memberTab === "profile" ? (
              <div className="crm-drawer__footer">
                <button type="button" className="crm-drawer__secondary" onClick={() => setEditing(null)}>Cancel</button>
                <button type="submit" className="crm-drawer__primary" disabled={updateRecord.isPending} style={{ display: "inline-flex", alignItems: "center", gap: ".4rem" }}>
                  {updateRecord.isPending ? <><Loader2 size={13} className="animate-spin" /> Saving</> : "Save changes"}
                </button>
              </div>
            ) : (
              <div className="crm-drawer__footer" style={{ justifyContent: memberTab === "billing" ? "space-between" : "flex-end" }}>
                {/*
                  Download sits hard left, apart from Close, and names how many
                  it will fetch. A button that says "Download invoice" while
                  three are ticked is describing the wrong action.
                */}
                {memberTab === "billing" && (
                  <button
                    type="button"
                    className="crm-drawer__secondary"
                    disabled={selectedInvoices.length === 0}
                    style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", opacity: selectedInvoices.length === 0 ? .5 : 1, cursor: selectedInvoices.length === 0 ? "not-allowed" : "pointer" }}
                    title={selectedInvoices.length === 0 ? "Tick an invoice to download it" : undefined}
                    onClick={() => downloadSelectedInvoices()}
                  >
                    <FileText size={13} />
                    {selectedInvoices.length > 1 ? `Download ${selectedInvoices.length} invoices` : "Download invoice"}
                  </button>
                )}
                <button type="button" className="crm-drawer__secondary" onClick={() => setEditing(null)}>Close</button>
              </div>
            )}
          </form>
        </SheetContent>
      </Sheet>

      <AlertDialog open={Boolean(pendingArchive)} onOpenChange={open => { if (!open) setPendingArchive(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {pendingArchive?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              <span style={{ display: "block" }}>This preserves their history but changes their account access immediately.</span>
              <span style={{ display: "block", marginTop: ".8rem", padding: ".7rem .8rem", borderRadius: ".5rem", background: "rgba(255,255,255,.045)", color: "#CBD5E1", lineHeight: 1.55 }}>
                <strong style={{ display: "block", marginBottom: ".3rem", color: "#E2E8F0" }}>What will happen</strong>
                They will no longer be able to sign in, and they will be removed from the member directory and the Current members list. Their attendee profile, registrations, and registration notifications are archived and hidden from normal site and CRM views, but remain retained for restoration.
              </span>
              {pendingArchive?.hasBillingSubscription ? (
                <span role="alert" style={{ display: "block", marginTop: ".75rem", padding: ".7rem .8rem", borderRadius: ".5rem", background: "rgba(245,158,11,.1)", border: "1px solid rgba(245,158,11,.28)", color: "#FDE68A", lineHeight: 1.55 }}>
                  <strong style={{ display: "block", marginBottom: ".2rem" }}>Cancel the Stripe subscription first.</strong>
                  This member still has a membership that may be billing. Archive is unavailable until its Stripe subscription has been cancelled, so they are never charged for an account they cannot use.
                </span>
              ) : (
                <span style={{ display: "block", marginTop: ".75rem" }}>Before continuing, confirm that this member has no active Stripe subscription. This action cannot be undone from the CRM.</span>
              )}
              {archiveError && <span role="alert" style={{ display: "block", marginTop: ".75rem", color: "#FDA4AF", fontWeight: 650 }}>{archiveError}</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveMember.isPending}>Keep member</AlertDialogCancel>
            <AlertDialogAction
              disabled={archiveMember.isPending || Boolean(pendingArchive?.hasBillingSubscription)}
              onClick={event => {
                // The dialog stays open until the server answers, so a refusal —
                // an active subscription, most often — is read in context.
                event.preventDefault();
                if (pendingArchive && !pendingArchive.hasBillingSubscription) archiveMember.mutate({ userOpenId: pendingArchive.userOpenId });
              }}
            >
              {archiveMember.isPending ? "Archiving…" : pendingArchive?.hasBillingSubscription ? "Subscription must be cancelled" : "Archive member"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
